import { UploadFile } from '@mui/icons-material';
import {
  FormControl,
  Typography,
  FormHelperText,
  Button,
  Box,
  Modal,
} from '@mui/material';
import { useCallback, useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { useController, Control, Path, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { useImageCompression } from '../hooks/useImageCompression';

interface Props<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  label: string;
  maxWidth?: number;
  maxHeight?: number;
  aspectRatio?: number; // Optional aspect ratio constraint
}

// Helper function to apply transformations to an image and return a data URL
const applyTransformationsToImage = async (
  file: File,
  rotation: number,
  flipHorizontal: boolean,
  flipVertical: boolean
): Promise<string> => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          throw new Error('No 2d context for transformation');
        }

        let { width, height } = img;

        // Adjust canvas dimensions for rotation
        if (rotation % 180 !== 0) {
          [width, height] = [height, width];
        }

        canvas.width = width;
        canvas.height = height;

        ctx.save();
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((rotation * Math.PI) / 180);
        ctx.scale(flipHorizontal ? -1 : 1, flipVertical ? -1 : 1);
        ctx.drawImage(img, -img.width / 2, -img.height / 2);
        ctx.restore();

        resolve(canvas.toDataURL(file.type));
      };
      img.src = event.target?.result as string;
    };
    reader.readAsDataURL(file);
  });
};

// Helper function to get crop as canvas
function getCroppedImg(
  image: HTMLImageElement,
  crop: PixelCrop,
  fileName: string
): Promise<{ file: File; preview: string }> {
  const canvas = document.createElement('canvas');
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  canvas.width = crop.width;
  canvas.height = crop.height;

  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('No 2d context');
  }

  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0,
    0,
    crop.width,
    crop.height
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        throw new Error('Canvas is empty');
      }

      const file = new File([blob], fileName, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });

      const preview = URL.createObjectURL(file);
      resolve({ file, preview });
    }, 'image/jpeg');
  });
}

export default function AppDropzone<T extends FieldValues>({
  control,
  name,
  label,
  maxWidth = 800,
  maxHeight = 800,
  aspectRatio,
}: Props<T>) {
  const { fieldState, field } = useController<T>({
    control,
    name,
    defaultValue: null as T[Path<T>],
  });
  const { t } = useTranslation();
  const { compressImage } = useImageCompression();

  // State for crop functionality
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [imgSrc, setImgSrc] = useState('');
  const [imgRef, setImgRef] = useState<HTMLImageElement | null>(null);
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    width: 90,
    height: 90,
    x: 5,
    y: 5,
  });
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [originalFile, setOriginalFile] = useState<File | null>(null);
  const [rotation, setRotation] = useState(0);
  const [flipHorizontal, setFlipHorizontal] = useState(false);
  const [flipVertical, setFlipVertical] = useState(false);

  // Effect to apply transformations when rotation/flip states change
  useEffect(() => {
    if (originalFile) {
      applyTransformationsToImage(
        originalFile,
        rotation,
        flipHorizontal,
        flipVertical
      ).then((dataUrl) => {
        setImgSrc(dataUrl);
      });
    }
  }, [originalFile, rotation, flipHorizontal, flipVertical]);

  const dzStyles = {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    padding: '20px',
    height: '200px',
    width: '100%',
    maxWidth: 500,
    border: '2px dashed',
    borderColor: fieldState.error ? '#ff1744' : '#eee',
    borderRadius: '8px',
    backgroundColor: '#fafafa',
    color: '#bdbdbd',
    transition: 'border .24s ease-in-out',
    cursor: 'pointer',
    '&:hover': {
      borderColor: '#2196f3',
      backgroundColor: '#f5f5f5',
    },
  };

  const dzActive = {
    borderColor: '#2196f3',
    backgroundColor: '#f0f8ff',
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];

        // Check if the file is an SVG
        if (file.type === 'image/svg+xml') {
          field.onChange(
            Object.assign(file, {
              preview: URL.createObjectURL(file),
            })
          );
          return; // Skip cropping for SVG
        }

        // Only process if it's a raster image
        if (file.type.startsWith('image/')) {
          setOriginalFile(file);
          // Apply initial transformations (none) to get the base image for cropping
          applyTransformationsToImage(file, 0, false, false).then((dataUrl) => {
            setImgSrc(dataUrl);
            setCropModalOpen(true);
          });
        } else {
          // For other non-image files, just use as is
          field.onChange(
            Object.assign(file, {
              preview: URL.createObjectURL(file),
            })
          );
        }
      }
    },
    [field]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': [],
    },
  });

  // Called when the user loads the image in the crop UI
  const onImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget;
      setImgRef(img);
      // Initialize crop based on aspect ratio if provided
      if (aspectRatio) {
        const imageWidth = img.width;

        const imageHeight = img.height;
        if (imageWidth && imageHeight) {
          let width = imageWidth;

          let height = imageWidth / aspectRatio;

          if (imageHeight < height) {
            width = imageHeight * aspectRatio;
            height = imageHeight;
          }

          width = width * 0.8;
          height = height * 0.8;
          const xOffset = (imageWidth - width) / 2;
          const yOffset = (imageHeight - height) / 2;

          const crop: Crop = {
            unit: 'px',
            x: xOffset,
            y: yOffset,
            width,
            height,
          };

          setCrop(crop);
          setCompletedCrop(crop as PixelCrop);
        }
      } else {
        // Default crop - 80% of the image
        setCrop({
          unit: '%',
          x: 10,
          y: 10,
          width: 80,
          height: 80,
        });
        setCompletedCrop(undefined);
      }
    },
    [aspectRatio]
  );

  // Apply crop and resize when user confirms
  const handleCropComplete = async () => {
    if (!imgRef || !completedCrop || !originalFile) return;

    try {
      // First apply crop
      const { file: croppedFile } = await getCroppedImg(
        imgRef,
        completedCrop,
        originalFile.name
      );

      // Then compress/resize using the hook
      const compressedFile = await compressImage(croppedFile, {
        maxWidth,
        maxHeight,
      });

      if (compressedFile) {
        const finalFile = new File([compressedFile], compressedFile.name, {
          type: compressedFile.type,
        });
        Object.assign(finalFile, { preview: URL.createObjectURL(finalFile) });
        field.onChange(finalFile);
      }

      // Close modal
      setCropModalOpen(false);
      setImgSrc('');
      setOriginalFile(null);
    } catch (error) {
      console.error('Error processing image:', error);
    }
  };

  // Handle cancel
  const handleCropCancel = () => {
    setCropModalOpen(false);
    setImgSrc('');
    setOriginalFile(null);
  };

  const handleRotate = () => {
    setRotation((prev) => prev + 90);
  };

  const handleFlipHorizontal = () => {
    setFlipHorizontal((prev) => !prev);
  };

  const handleFlipVertical = () => {
    setFlipVertical((prev) => !prev);
  };

  return (
    <>
      <div {...getRootProps()}>
        <FormControl
          style={isDragActive ? { ...dzStyles, ...dzActive } : dzStyles}
          error={!!fieldState.error}
        >
          <input {...getInputProps()} />
          <UploadFile sx={{ fontSize: '64px', mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            {label}
          </Typography>
          <Typography variant="body2" color="textSecondary" align="center">
            {t('common.drop-image-description')}
          </Typography>
          <FormHelperText>{fieldState.error?.message}</FormHelperText>
        </FormControl>
      </div>

      {/* Crop Modal */}
      <Modal
        open={cropModalOpen}
        onClose={handleCropCancel}
        aria-labelledby="crop-modal-title"
        aria-describedby="crop-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: '80%', md: '70%' },
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            border: '2px solid #000',
            boxShadow: 24,
            p: 4,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Typography id="crop-modal-title" variant="h6" component="h2">
            {t('common.crop-image')}
          </Typography>

          <Box
            sx={{
              overflowX: 'auto',
              textAlign: 'center',
              position: 'relative',
            }}
          >
            {imgSrc && (
              <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={aspectRatio}
              >
                <img
                  src={imgSrc}
                  onLoad={onImageLoad}
                  alt="Crop"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '60vh',
                  }}
                />
              </ReactCrop>
            )}
          </Box>

          <Box
            sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}
          >
            <Button onClick={handleCropCancel} variant="outlined">
              {t('common.cancel')}
            </Button>
            <Button onClick={handleRotate} variant="contained">
              Rotate
            </Button>
            <Button onClick={handleFlipHorizontal} variant="contained">
              Flip Horizontal
            </Button>
            <Button onClick={handleFlipVertical} variant="contained">
              Flip Vertical
            </Button>
            <Button
              onClick={handleCropComplete}
              variant="contained"
              disabled={!completedCrop?.width || !completedCrop?.height}
            >
              {t('common.apply')}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}
